/**
 * Token types shown when deploying an asset-class contract (offline flow).
 * CBC* labels are the platform naming; ercStandard is the familiar EVM interface family.
 */

export const DEPLOY_TOKEN_ARTIFACT_MODE = {
  /** Preloaded ERC-721 series bytecode in-repo */
  PREBUILT_ERC721: 'prebuilt_erc721',
  /** Preloaded CBC20-style ERC-20 catalog bytecode */
  PREBUILT_ERC20: 'prebuilt_erc20',
  /** Preloaded CBC1155-style ERC-1155 catalog bytecode */
  PREBUILT_ERC1155: 'prebuilt_erc1155',
  /** User supplies ABI + creation bytecode (ERC-777, ERC-4626, ERC-3525, other) */
  CUSTOM: 'custom'
};

/**
 * @typedef {object} DeployTokenTypeDef
 * @property {string} id
 * @property {'circle'|'hexagon'|'layers'|'zap'|'trending'|'grid'|'settings'} icon
 * @property {string} name
 * @property {string} brandStandard
 * @property {string} ercStandard
 * @property {string} description
 * @property {boolean} divisible
 * @property {boolean} unique
 * @property {'prebuilt_erc721'|'prebuilt_erc20'|'prebuilt_erc1155'|'custom'} artifactMode
 */

/** @type {DeployTokenTypeDef[]} */
export const DEPLOY_TOKEN_TYPES = [
  {
    id: 'fungible',
    icon: 'circle',
    name: 'Fungible Token (FT)',
    brandStandard: 'CBC20',
    ercStandard: 'ERC-20',
    description: 'CBC20 — divisible tokens for commodities and securities (ERC-20 compatible).',
    divisible: true,
    unique: false,
    artifactMode: DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC20
  },
  {
    id: 'nonFungible',
    icon: 'hexagon',
    name: 'Non-Fungible Token (NFT)',
    brandStandard: 'CBC721',
    ercStandard: 'ERC-721',
    description: 'CBC721 — unique tokens for individual assets (ERC-721 compatible).',
    divisible: false,
    unique: true,
    artifactMode: DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC721
  },
  {
    id: 'fractionalNFT',
    icon: 'layers',
    name: 'Fractional NFT',
    brandStandard: 'CBC1155',
    ercStandard: 'ERC-1155',
    description: 'CBC1155 — fractional ownership and multi-token collections (ERC-1155 compatible).',
    divisible: true,
    unique: true,
    artifactMode: DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC1155
  },
  {
    id: 'erc777',
    icon: 'zap',
    name: 'Fungible with hooks',
    brandStandard: 'ERC-777',
    ercStandard: 'ERC-777',
    description: 'Advanced fungible tokens with operator lists and receive hooks — common in modern DeFi stacks.',
    divisible: true,
    unique: false,
    artifactMode: DEPLOY_TOKEN_ARTIFACT_MODE.CUSTOM
  },
  {
    id: 'erc4626',
    icon: 'trending',
    name: 'Vault share',
    brandStandard: 'ERC-4626',
    ercStandard: 'ERC-4626',
    description: 'Tokenized vault shares — standard interface for yield vaults and custody wrappers.',
    divisible: true,
    unique: false,
    artifactMode: DEPLOY_TOKEN_ARTIFACT_MODE.CUSTOM
  },
  {
    id: 'erc3525',
    icon: 'grid',
    name: 'Semi-fungible',
    brandStandard: 'ERC-3525',
    ercStandard: 'ERC-3525',
    description: 'Slot-based semi-fungible certificates — used for carbon, bonds, and structured positions.',
    divisible: true,
    unique: true,
    artifactMode: DEPLOY_TOKEN_ARTIFACT_MODE.CUSTOM
  },
  {
    id: 'customContract',
    icon: 'settings',
    name: 'Other / custom',
    brandStandard: 'Custom',
    ercStandard: 'Various',
    description: 'Any other EVM-compatible standard — paste your audited ABI and creation bytecode.',
    divisible: true,
    unique: false,
    artifactMode: DEPLOY_TOKEN_ARTIFACT_MODE.CUSTOM
  }
];

/** @param {{ tokenStandard?: string } | null | undefined} [assetClass] */
export function getDefaultDeployTokenTypeId(assetClass) {
  const ts = String(assetClass?.tokenStandard ?? '').toUpperCase();
  if (ts === 'ERC1155' || ts === '2') return 'fractionalNFT';
  if (ts === 'ERC20' || ts === '3') return 'fungible';
  return 'nonFungible';
}

/**
 * API enum string the backend persists on the asset class after deploy (JsonStringEnumConverter).
 * Undefined for custom / unknown bytecode — server keeps existing TokenStandard.
 * @param {string | undefined} deployTokenTypeId
 * @returns {'ERC721'|'ERC20'|'ERC1155'|undefined}
 */
export function getDeclaredTokenStandardForDeployTokenTypeId(deployTokenTypeId) {
  if (!deployTokenTypeId) return undefined;
  const t = getDeployTokenType(deployTokenTypeId);
  if (!t) return undefined;
  switch (t.artifactMode) {
    case DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC721:
      return 'ERC721';
    case DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC20:
      return 'ERC20';
    case DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC1155:
      return 'ERC1155';
    default:
      return undefined;
  }
}

/** @param {string} id */
export function getDeployTokenType(id) {
  return DEPLOY_TOKEN_TYPES.find((t) => t.id === id) || DEPLOY_TOKEN_TYPES.find((t) => t.id === 'nonFungible');
}

/** @param {DeployTokenTypeDef} type */
export function deployTokenTypeUsesPrebuiltErc721(type) {
  return type?.artifactMode === DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC721;
}

/** True when the UI should use an in-repo ABI + bytecode (CBC721 / CBC20 / CBC1155). */
export function deployTokenTypeUsesPlatformPrebuiltArtifact(type) {
  const m = type?.artifactMode;
  return (
    m === DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC721 ||
    m === DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC20 ||
    m === DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC1155
  );
}
